import React from 'react';
import { Phone, Mail, MapPin, CheckCircle2 } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const serviceAreas = [
    "Montauk", "Amagansett", "East Hampton", "Springs", "Wainscott", "Sag Harbor",
    "North Haven", "Bridgehampton", "Water Mill", "Sagaponack", "Southampton",
    "Shinnecock Hills", "North Sea", "Flanders", "Hampton Bays", "East Quogue",
    "Quiogue", "Quogue", "Remsenburg", "Speonk", "Westhampton", "Westhampton Beach",
    "Riverhead", "Aquebogue", "Jamesport", "Laurel", "Mattituck", "Cutchogue",
    "Greenport", "Southold"
  ];

  const services = [
    "Window Cleaning",
    "House Washing",
    "Roof Washing",
    "Interior Cleaning",
    "Commercial Cleaning",
    "Gutter Cleaning",
    "Shrink Wrapping"
  ];

  return (
    <footer id="contact" className="bg-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/9u4lf7am_logo%20transparency-01.png"
              alt="Reflections Cleanings"
              className="h-16 sm:h-20 w-auto mb-6"
            />
            <p className="text-gray-400 mb-6 leading-relaxed">
              Locally owned, family-operated. Serving Long Island and the Hamptons with pride and care.
            </p>
            <div className="space-y-3">
              <a href="tel:631-660-8164" className="flex items-center gap-3 text-gray-400 hover:text-blue-400 transition-colors">
                <Phone className="w-5 h-5" />
                <span>(631) 660-8164</span>
              </a>
              <a href="mailto:info@refcleanings.com" className="flex items-center gap-3 text-gray-400 hover:text-blue-400 transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@refcleanings.com</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <button
                    onClick={() => {
                      const element = document.querySelector('#services');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => {
                    const element = document.querySelector('#services');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.querySelector('#quote');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Get a Quote
                </button>
              </li>
              <li>
                <a href="#testimonials" className="text-gray-400 hover:text-blue-400 transition-colors">
                  Testimonials
                </a>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Service Areas
            </h4>
            <p className="text-gray-400 mb-3 text-sm">
              Proudly serving Long Island and the Hamptons:
            </p>
            {/* Hamptons Town Links */}
            <p className="text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">The Hamptons</p>
            <div className="space-y-1.5 mb-4">
              {[
                { name: 'Southampton', slug: '/locations/southampton' },
                { name: 'East Hampton', slug: '/locations/east-hampton' },
                { name: 'Bridgehampton', slug: '/locations/bridgehampton' },
                { name: 'Sag Harbor', slug: '/locations/sag-harbor' },
                { name: 'Water Mill', slug: '/locations/water-mill' },
                { name: 'Westhampton', slug: '/locations/westhampton' },
                { name: 'Remsenburg', slug: '/locations/remsenburg' }
              ].map((town) => (
                <a
                  key={town.slug}
                  href={town.slug}
                  className="block text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  &rarr; {town.name}
                </a>
              ))}
            </div>
            {/* Nassau County Town Links */}
            <p className="text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">Nassau County</p>
            <div className="space-y-1.5 mb-4">
              <a
                href="/nassau-county-ny"
                className="block text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold"
              >
                &rarr; All Nassau County Areas
              </a>
              {[
                { name: 'Old Westbury', slug: '/old-westbury-ny' },
                { name: 'Brookville', slug: '/brookville-ny' },
                { name: 'Muttontown', slug: '/muttontown-ny' },
                { name: 'Sands Point', slug: '/sands-point-ny' },
                { name: 'Oyster Bay Cove', slug: '/oyster-bay-cove-ny' },
                { name: 'Plandome', slug: '/plandome-ny' },
                { name: 'Hewlett Bay Park', slug: '/hewlett-bay-park-ny' }
              ].map((town) => (
                <a
                  key={town.slug}
                  href={town.slug}
                  className="block text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  &rarr; {town.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Reflections Cleanings. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-500 hover:text-blue-400 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-400 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1f2937;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
      `}</style>
    </footer>
  );
};

export default Footer;
